import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import moment from 'moment';

const useStyles = makeStyles(() => ({
	cell: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 190,
		padding: '6px 0',
		gap: 4
	},
	topRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 8,
		minHeight: 18
	},
	date: {
		fontSize: '0.6875rem',
		fontWeight: 600,
		letterSpacing: '0.06em',
		color: 'oklch(0.7 0.005 95)',
		textTransform: 'uppercase',
		lineHeight: 1.2,
		fontVariantNumeric: 'tabular-nums'
	},
	time: {
		fontSize: '0.95rem',
		fontWeight: 600,
		color: 'oklch(0.96 0.004 95)',
		lineHeight: 1.25,
		fontVariantNumeric: 'tabular-nums',
		letterSpacing: '-0.005em'
	},
	pill: {
		display: 'inline-flex',
		alignItems: 'center',
		fontSize: '0.6875rem',
		fontWeight: 600,
		padding: '1px 8px',
		borderRadius: 999,
		lineHeight: 1.55,
		whiteSpace: 'nowrap',
		fontVariantNumeric: 'tabular-nums',
		letterSpacing: '0.01em',
		transition: 'background-color 180ms cubic-bezier(0.16, 1, 0.3, 1)'
	},
	pillAlert: {
		backgroundColor: 'oklch(0.88 0.07 27)',
		color: 'oklch(0.42 0.18 27)',
		boxShadow: 'inset 0 0 0 1px oklch(0.78 0.1 27)'
	},
	pillNormal: {
		backgroundColor: 'oklch(0.88 0.13 145)',
		color: 'oklch(0.28 0.09 145)',
		boxShadow: 'inset 0 0 0 1px oklch(0.78 0.14 145)'
	},
	coord: {
		display: 'inline-block',
		fontSize: '0.6875rem',
		fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
		color: 'oklch(0.72 0.005 95)',
		padding: '1px 0',
		marginTop: 2,
		textDecoration: 'none',
		letterSpacing: '-0.008em',
		fontVariantNumeric: 'tabular-nums',
		width: 'fit-content',
		borderBottom: '1px dashed oklch(0.45 0.005 95)',
		transition: 'color 160ms ease-out, border-color 160ms ease-out',
		'&:hover': {
			color: 'oklch(0.95 0.005 95)',
			borderBottomColor: 'oklch(0.78 0.005 95)',
			borderBottomStyle: 'solid'
		},
		'&:focus-visible': {
			outline: '2px solid oklch(0.92 0.18 95)',
			outlineOffset: 2,
			borderRadius: 2
		}
	},
	empty: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		minHeight: 56,
		fontSize: '1rem',
		fontWeight: 300,
		color: 'oklch(0.5 0.004 95)',
		letterSpacing: '0.08em',
		paddingLeft: 2
	}
}));

const isEmptyTime = (t) => !t || t === '0000-00-00 00:00:00';

const parseDistanceMeters = (raw) => {
	if (!raw || raw === '-') return null;
	const num = parseFloat(String(raw).replace(' m', ''));
	return isNaN(num) ? null : num;
};

const formatDistanceDisplay = (raw) => {
	const num = parseDistanceMeters(raw);
	if (num === null) return null;
	if (num >= 1000) return (num / 1000).toFixed(2) + ' km';
	return Math.round(num) + ' m';
};

const CheckDetailsCell = ({type, row}) => {
	const classes = useStyles();
	const isCheckin = type === 'checkin';

	const time = isCheckin ? row.checkin_time : row.checkout_time;
	const lat = isCheckin ? row.location_lat : row.checkout_lat;
	const lng = isCheckin ? row.location_lng : row.checkout_lng;
	const distRaw = isCheckin ? row.distance : row.checkout_distance;

	if (isEmptyTime(time)) {
		return <div className={classes.empty}>—</div>;
	}

	const dt = moment(time, 'YYYY-MM-DD HH:mm:ss');
	const distDisplay = formatDistanceDisplay(distRaw);
	const distMeters = parseDistanceMeters(distRaw);
	const alert = distMeters !== null && distMeters >= 500;
	const hasCoord = lat && lng;

	return (
		<div className={classes.cell}>
			<div className={classes.topRow}>
				<span className={classes.date}>{dt.format('DD MMM YYYY')}</span>
				{distDisplay && (
					<span className={`${classes.pill} ${alert ? classes.pillAlert : classes.pillNormal}`}>
						{distDisplay}
					</span>
				)}
			</div>
			<span className={classes.time}>{dt.format('h:mm A')}</span>
			{hasCoord && (
				<a
					href={`https://www.google.com/maps?q=${lat},${lng}`}
					target="_blank"
					rel="noopener noreferrer"
					className={classes.coord}
					onClick={(e) => e.stopPropagation()}
					title={`${lat}, ${lng}`}
				>
					{Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}
				</a>
			)}
		</div>
	);
};

export default CheckDetailsCell;
